import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Flex, InputNumber } from 'antd';
import { CheckOutlined, EditOutlined } from '@ant-design/icons';
import {
  ANNUAL_CONTRACT_DISCOUNT,
  clampDiscount,
  isDiscountInRange,
} from '../../services/helpers/ContractDiscountHelpers';

/**
 * "Annual contract discount" row — a manager with the EditAnnualContractDiscount
 * permission grants a discount (default 10%) on an annual commitment term. The
 * upper bound is `max` (0–15% for most managers, 0–20% for admins / sales
 * leadership). Read-only for everyone else.
 */
export default function AnnualContractDiscount({
  value,
  onChange,
  editable = false,
  max = ANNUAL_CONTRACT_DISCOUNT.max,
  className,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Billing:AnnualContractDiscount' });

  const [isEditing, setIsEditing] = useState(false);

  const commit = () => {
    if (!isDiscountInRange(value, max)) {
      return toast.error(t('toasts.invalid_discount', { min: ANNUAL_CONTRACT_DISCOUNT.min, max }));
    }

    onChange(clampDiscount(value, max));
    setIsEditing(false);
  };

  return (
    <div
      className={`border border-solid border-brand-inkGrey-grey_2 rounded-lg p-md flex justify-between items-center ${className ?? ''}`}
    >
      <h5 className="mb-0">{t('title')}</h5>
      <Flex gap="8px" align="center">
        <InputNumber
          min={ANNUAL_CONTRACT_DISCOUNT.min}
          max={max}
          step={1}
          precision={0}
          value={value}
          suffix="%"
          disabled={!editable || !isEditing}
          onChange={(next) => onChange(next)}
          onBlur={() => onChange(clampDiscount(value, max))}
          onPressEnter={commit}
        />
        {editable && (
          <Button
            type={isEditing ? 'primary' : 'default'}
            icon={isEditing ? <CheckOutlined /> : <EditOutlined />}
            onClick={() => (isEditing ? commit() : setIsEditing(true))}
            aria-label={t(isEditing ? 'buttons.save' : 'buttons.edit')}
          />
        )}
      </Flex>
    </div>
  );
}
